import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, type ProjectDetail } from "../api.js";
import { AppShell } from "../components/AppShell.js";
import { ProjectNav } from "../components/ProjectNav.js";
import { shareStatus } from "../components/projectPages.js";
import { ErrorNote, Spinner } from "../components/ui.js";
import { ShareStats } from "../share/ShareStats.js";

/**
 * Who watched the shared link and the embeds: a page of its own, one click
 * from the editor in the project nav, rather than a tab under Export. The
 * same for a product video and a launch film.
 */
export const Views: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [share, setShare] = useState<{ published: boolean; emailGate: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    void Promise.all([api.getProject(id), api.share(id)])
      .then(([detail, settings]) => {
        if (!current) return;
        setProject(detail);
        setShare({ published: settings.published, emailGate: settings.emailGate });
      })
      .catch((cause: unknown) => {
        if (current) setError(cause instanceof Error ? cause.message : "Could not load this project");
      });
    return () => {
      current = false;
    };
  }, [id]);

  const status = shareStatus(share?.published ?? null);

  return (
    <AppShell
      wide
      breadcrumb={
        <>
          <Link to="/dashboard" className="shrink-0 text-ink-400 transition-colors hover:text-ink-200">
            Projects
          </Link>
          <span className="shrink-0 text-ink-400">/</span>
          <span className="min-w-0 truncate font-medium text-ink-100">{project?.name ?? "…"}</span>
        </>
      }
      nav={project ? <ProjectNav projectId={id} kind={project.kind} current="views" /> : null}
    >
      <div className="mx-auto flex max-w-[1120px] flex-col gap-5">
        <div className="flex flex-col gap-1">
          <h1 className="text-[28px] font-semibold tracking-[-0.01em] text-ink-100">Views</h1>
          <p className="text-[14px] text-ink-300">
            Who watched the shared link and the embeds.{" "}
            {status ? (
              <span className={share?.published ? "font-semibold text-[#1C6B34]" : "font-semibold text-[#9A3A00]"}>
                {share?.published ? "● Live" : "● Draft — not shared right now."}
              </span>
            ) : null}
            {share && !share.published ? (
              <>
                {" "}
                <Link to={`/projects/${id}/render?tab=share`} className="font-semibold text-ember-600 hover:underline">
                  Publish it
                </Link>
              </>
            ) : null}
          </p>
        </div>

        {error ? <ErrorNote>{error}</ErrorNote> : null}
        {project && share ? (
          <section className="panel p-5">
            <ShareStats projectId={id} projectName={project.name} emailGate={share.emailGate} />
          </section>
        ) : error ? null : (
          <div className="flex justify-center p-10" role="status" aria-label="Loading views">
            <Spinner />
          </div>
        )}
      </div>
    </AppShell>
  );
};
